import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { createTask, getUsers, getTags, getResponsabilidades } from '../utils/api';
import { motion, AnimatePresence } from 'framer-motion';

const PRIORITY_CONFIG = {
  'Crítica': { color: '#EF4444', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.35)' },
  'Alta':    { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.35)' },
  'Media':   { color: '#3B82F6', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.35)' },
  'Baja':    { color: '#64748B', bg: 'rgba(100,116,139,0.10)', border: 'rgba(100,116,139,0.25)' },
};

const CreateTask = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();

  const isManager = user?.role === 'Presidencia' || user?.role === 'Gerencia';

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [responsabilidades, setResponsabilidades] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [checklistInput, setChecklistInput] = useState('');
  const [respMode, setRespMode] = useState('select'); // 'select' | 'new'

  const [form, setForm] = useState({
    responsabilidad: '',
    title: '',
    description: '',
    objetivo: '',
    meta: '',
    responsible_user_id: isManager ? '' : (user?.id || ''),
    priority: 'Media',
    due_date: '',
    collaborator_ids: [],
    checklist: [],
    tags: [],
    is_recurrent: false,
    visibility: 'Shared',
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    const [usersRes, tagsRes, respRes] = await Promise.allSettled([getUsers(), getTags(), getResponsabilidades()]);
    if (usersRes.status === 'fulfilled') setUsers(usersRes.value.data || []);
    if (tagsRes.status === 'fulfilled') setTags(tagsRes.value.data || []);
    if (respRes.status === 'fulfilled') {
      const list = respRes.value.data || [];
      setResponsabilidades(list);
      if (list.length === 0) setRespMode('new');
    }
  };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const toggleCollaborator = (id) => {
    set('collaborator_ids',
      form.collaborator_ids.includes(id)
        ? form.collaborator_ids.filter(x => x !== id)
        : [...form.collaborator_ids, id]
    );
  };

  const toggleTag = (id) => {
    set('tags', form.tags.includes(id) ? form.tags.filter(x => x !== id) : [...form.tags, id]);
  };

  const addChecklist = () => {
    if (!checklistInput.trim()) return;
    set('checklist', [...form.checklist, { id: `tmp-${Date.now()}`, text: checklistInput.trim(), completed: false }]);
    setChecklistInput('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.responsabilidad.trim()) { addToast('La responsabilidad es requerida', 'error'); return; }
    if (!form.title.trim()) { addToast('La tarea es requerida', 'error'); return; }
    if (!form.objetivo.trim()) { addToast('El objetivo es requerido', 'error'); return; }
    if (!form.meta.trim()) { addToast('La meta es requerida', 'error'); return; }
    if (isManager && !form.responsible_user_id) { addToast('Selecciona un responsable', 'error'); return; }

    setLoading(true);
    try {
      await createTask({
        ...form,
        responsible_user_id: form.responsible_user_id || user.id,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        visibility: isManager ? 'Shared' : form.visibility,
        status: form.is_recurrent ? 'Recurrente' : 'Pendiente',
      });
      addToast('Tarea creada', 'success');
      navigate('/tasks');
    } catch (err) {
      addToast(err.response?.data?.detail || 'Error al crear tarea', 'error');
    } finally {
      setLoading(false);
    }
  };

  const selectedUser = users.find(u => u.id === form.responsible_user_id);
  const advancedCount = form.collaborator_ids.length + form.tags.length + form.checklist.length + (form.is_recurrent ? 1 : 0) + (form.due_date ? 1 : 0) + (form.description.trim() ? 1 : 0);

  return (
    <div className="min-h-screen">

      {/* Topbar */}
      <div className="px-6 py-4 border-b border-border bg-background sticky top-14 md:top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-lg font-bold text-foreground">Nueva Tarea</h1>
            <p className="text-xs text-muted-foreground">Lo esencial primero — el resto se puede editar después</p>
          </div>
        </div>
      </div>

      <div className="p-5 max-w-xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Responsabilidad */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wide">
                Responsabilidad <span className="text-destructive">*</span>
              </label>
              {responsabilidades.length > 0 && (
                <button type="button"
                  onClick={() => { setRespMode(m => m === 'select' ? 'new' : 'select'); set('responsabilidad', ''); }}
                  className="text-xs font-semibold"
                  style={{ color: '#6366F1' }}>
                  {respMode === 'select' ? '+ Nueva responsabilidad' : '← Seleccionar existente'}
                </button>
              )}
            </div>
            {respMode === 'select' && responsabilidades.length > 0 ? (
              <div className="relative">
                <select
                  value={form.responsabilidad}
                  onChange={e => set('responsabilidad', e.target.value)}
                  required
                  className="input-decko w-full appearance-none pr-8"
                  autoFocus
                >
                  <option value="">Seleccionar responsabilidad...</option>
                  {responsabilidades.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            ) : (
              <input
                type="text"
                value={form.responsabilidad}
                onChange={e => set('responsabilidad', e.target.value)}
                className="input-decko w-full"
                placeholder="Ej: Desarrollo de cuentas PATRIOT"
                required
                autoFocus={respMode === 'new'}
              />
            )}
            <p className="text-xs text-muted-foreground mt-1">Agrupa esta tarea dentro de un área de responsabilidad</p>
          </div>

          {/* Tarea */}
          <div>
            <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1.5">
              Tarea <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={e => set('title', e.target.value)}
              className="input-decko w-full text-base"
              placeholder="¿Qué hay que hacer específicamente?"
              required
            />
          </div>

          {/* Objetivo y Meta */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1.5">
                Objetivo <span className="text-destructive">*</span>
              </label>
              <textarea
                value={form.objetivo}
                onChange={e => set('objetivo', e.target.value)}
                className="input-decko w-full resize-none"
                rows="2"
                placeholder="¿Qué se quiere lograr con esta tarea?"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1.5">
                Meta <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={form.meta}
                onChange={e => set('meta', e.target.value)}
                className="input-decko w-full"
                placeholder="¿Cuál es el resultado esperado / indicador de éxito?"
                required
              />
            </div>
          </div>

          {/* Responsable — solo para managers */}
          {isManager && (
            <div>
              <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1.5">
                Responsable <span className="text-destructive">*</span>
              </label>
              <div className="relative">
                <select
                  value={form.responsible_user_id}
                  onChange={e => set('responsible_user_id', e.target.value)}
                  required
                  className="input-decko w-full appearance-none pr-8"
                >
                  <option value="">Seleccionar responsable...</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name}{u.department_name ? ` — ${u.department_name}` : ''}</option>
                  ))}
                </select>
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {selectedUser?.department_name && (
                <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block" />
                  {selectedUser.department_name}
                </p>
              )}
            </div>
          )}

          {/* Prioridad — chips visuales */}
          <div>
            <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1.5">
              Prioridad
            </label>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(PRIORITY_CONFIG).map(([p, cfg]) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => set('priority', p)}
                  className="rounded-xl text-xs font-bold text-center transition-all"
                  style={{ minHeight: 44 }}
                  style={{
                    background: form.priority === p ? cfg.bg : 'transparent',
                    border: `1px solid ${form.priority === p ? cfg.border : 'rgba(255,255,255,0.07)'}`,
                    color: form.priority === p ? cfg.color : '#475569',
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Opciones avanzadas */}
          <div className="rounded-xl border border-border/50 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowAdvanced(x => !x)}
              className="w-full px-4 py-3 flex items-center justify-between text-sm font-semibold hover:bg-white/[0.02] transition-colors"
            >
              <span className="text-muted-foreground">
                Opciones
                {advancedCount > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-primary/15 text-primary">
                    {advancedCount}
                  </span>
                )}
              </span>
              <svg className={`w-4 h-4 text-muted-foreground transition-transform ${showAdvanced ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <AnimatePresence initial={false}>
              {showAdvanced && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-5 pt-1 space-y-5 border-t border-border/50">

                    {/* Fecha límite */}
                    <div className="pt-3">
                      <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1.5">
                        Fecha límite
                      </label>
                      <input
                        type="date"
                        value={form.due_date}
                        onChange={e => set('due_date', e.target.value)}
                        className="input-decko w-full"
                      />
                    </div>

                    {/* Descripción */}
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1.5">
                        Descripción
                      </label>
                      <textarea
                        value={form.description}
                        onChange={e => set('description', e.target.value)}
                        className="input-decko w-full resize-none"
                        rows="3"
                        placeholder="Contexto, instrucciones, enlace relevante..."
                      />
                    </div>

                    {/* Colaboradores */}
                    {isManager && users.length > 0 && (
                      <div className="pt-3">
                        <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">
                          Colaboradores
                          {form.collaborator_ids.length > 0 && (
                            <span className="ml-1.5 text-primary font-bold">({form.collaborator_ids.length})</span>
                          )}
                        </label>
                        <div className="space-y-0.5 max-h-44 overflow-y-auto">
                          {users.filter(u => u.id !== form.responsible_user_id).map(u => (
                            <label key={u.id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary cursor-pointer">
                              <input
                                type="checkbox"
                                checked={form.collaborator_ids.includes(u.id)}
                                onChange={() => toggleCollaborator(u.id)}
                                className="w-4 h-4 flex-shrink-0"
                                style={{ accentColor: '#00C878' }}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">{u.name}</p>
                                {u.department_name && <p className="text-xs text-muted-foreground">{u.department_name}</p>}
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Etiquetas */}
                    {tags.length > 0 && (
                      <div>
                        <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">
                          Etiquetas
                        </label>
                        <div className="flex flex-wrap gap-1.5">
                          {tags.map(tag => {
                            const sel = form.tags.includes(tag.id);
                            return (
                              <button
                                key={tag.id}
                                type="button"
                                onClick={() => toggleTag(tag.id)}
                                className="px-3 py-1 rounded-full text-xs font-semibold transition-all"
                                style={{
                                  background: sel ? `${tag.color}20` : 'rgba(255,255,255,0.04)',
                                  border: `1px solid ${sel ? tag.color : 'rgba(255,255,255,0.08)'}`,
                                  color: sel ? tag.color : '#475569',
                                }}
                              >
                                {tag.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Checklist */}
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">
                        Checklist
                        {form.checklist.length > 0 && (
                          <span className="ml-1.5 text-primary font-bold">({form.checklist.length})</span>
                        )}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={checklistInput}
                          onChange={e => setChecklistInput(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addChecklist())}
                          className="input-decko flex-1 text-sm"
                          placeholder="Agregar paso y presionar Enter..."
                        />
                        <button type="button" onClick={addChecklist} className="btn-secondary px-3 text-sm font-bold">+</button>
                      </div>
                      {form.checklist.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {form.checklist.map((item, idx) => (
                            <div key={item.id} className="flex items-center gap-2 px-3 py-2 bg-secondary/40 rounded-lg">
                              <span className="text-muted-foreground text-xs tabular-nums w-4 flex-shrink-0">{idx + 1}.</span>
                              <span className="flex-1 text-sm text-foreground">{item.text}</span>
                              <button
                                type="button"
                                onClick={() => set('checklist', form.checklist.filter(i => i.id !== item.id))}
                                className="text-muted-foreground/50 hover:text-destructive transition-colors text-xs flex-shrink-0"
                              >✕</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Recurrencia */}
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2">
                        Recurrencia
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <button
                          type="button"
                          onClick={() => set('is_recurrent', !form.is_recurrent)}
                          className={`w-10 h-5 rounded-full transition-all flex items-center px-0.5 flex-shrink-0 ${form.is_recurrent ? 'bg-primary' : 'bg-secondary'}`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_recurrent ? 'translate-x-5' : 'translate-x-0'}`} />
                        </button>
                        <span className="text-sm text-foreground">Tarea recurrente</span>
                      </label>
                      {form.is_recurrent && (
                        <p className="text-xs text-muted-foreground mt-1.5">
                          🔁 Esta tarea tendrá estado <strong>Recurrente</strong> — se reporta semana a semana sin fecha de cierre fija.
                        </p>
                      )}
                    </div>

                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Visibilidad — solo para Usuario */}
          {!isManager && (
            <div className="decko-card px-4 py-3 flex items-center gap-3">
              <button
                type="button"
                onClick={() => set('visibility', form.visibility === 'Shared' ? 'Internal' : 'Shared')}
                className={`w-10 h-5 rounded-full transition-all flex items-center px-0.5 flex-shrink-0 ${form.visibility === 'Shared' ? 'bg-primary' : 'bg-secondary'}`}
              >
                <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${form.visibility === 'Shared' ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
              <div>
                <p className="text-sm font-semibold text-foreground">Visible para gerencia</p>
                <p className="text-xs text-muted-foreground">
                  {form.visibility === 'Shared' ? 'Gerencia puede ver esta tarea' : 'Solo visible para ti'}
                </p>
              </div>
            </div>
          )}

          {/* Acciones */}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={() => navigate(-1)} className="btn-secondary flex-1">
              Cancelar
            </button>
            <button type="submit" disabled={loading} className="btn-primary flex-1">
              {loading ? 'Creando...' : 'Crear Tarea'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default CreateTask;
